import { useFormik } from "formik";
import { useEffect, useState } from "react";
import MESSAGES from "../../../../common/data/constants/messages";
import { ISurvey } from "../../../../common/data/dtos/survey";
import { ISurveyHeader } from "../../../../common/data/dtos/survey-header";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import surveyHeaderService from "../../../../services/survey-header-service";

type SurveyHeaderFormValues = {
    id?: string;
    title: string;
    description: string;
};

interface Props {
    isModalOpened: boolean;
    selectedSurvey: ISurvey | null;
    selectedSurveyHeader: ISurveyHeader | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const SurveyHeaderModal = ({ isModalOpened, selectedSurvey, selectedSurveyHeader, onModalClose, onAfterSubmit }: Props) => {

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const formik = useFormik({
        initialValues: {
            title: '',
            description: '',
        },

        validate: (values) => {
            const errors: {
                description?: string;
            } = {};

            if (!values.description)
                errors.description = MESSAGES.VALIDATION.REQUIRED_FIELD;

            return errors;
        },

        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    useEffect(() => {

        resetForm();

        if (selectedSurveyHeader)
            setFieldValues(selectedSurveyHeader);

    }, [isModalOpened]);

    const onFormSubmit = async (values: SurveyHeaderFormValues) => {
        if (selectedSurveyHeader) {

            if (!selectedSurvey) {
                showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
                return;
            }

            setIsSubmitting(true);

            const response = await surveyHeaderService.updateSurveyHeader({
                id: selectedSurveyHeader.id,
                title: values.title,
                description: values.description,
                surveyId: selectedSurvey.id!,
                rowVersion: selectedSurveyHeader.rowVersion,
                status: selectedSurveyHeader.status,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.EDIT_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.EDITED);
        } else {
            setIsSubmitting(true);

            const response = await surveyHeaderService.createSurveyHeader({
                title: values.title,
                description: values.description,
                surveyId: selectedSurvey?.id,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.SAVE_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.SAVED);
        }

        resetForm();
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                title: '',
                description: '',
            },
        });
    };

    const setFieldValues = (values: ISurveyHeader) => {
        formik.setFieldValue('title', values.title);
        formik.setFieldValue('description', values.description);
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'lg'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='survey-header-modal'>Manutenção de cabeçalhos</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>
                    <CardTabItem id='tab-item-1' title='Dados do cabeçalho' icon='ViewHeadline'>
                        <div className='row g-4'>

                        <div className='col-lg-12 col-md-12 col-sm-12'>
                                <FormGroup id='title' label='Título' isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.title}
                                    />
                                </FormGroup>
                            </div>

                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <FormGroup id='description' label='Descrição' isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.description}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.description}
                                        invalidFeedback={formik.errors.description}
                                        validFeedback=''
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={formik.handleSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};